import { datadogLogs } from "@datadog/browser-logs"
import { ALL_SCOPES, Scope } from "common/lib/scope"
import jwtDecode from "jwt-decode"
import * as env from "lib/env"
import { AccessToken } from "types/idp"
import { localStorage, ACCESS_TOKEN_KEY } from "common/utils/localstorage"

export const getIdpRedirectUrl = (): string => {
    const query = new URLSearchParams({
        directory_hint: "onelogin",
        client_id: env.IDP_CLIENT_ID,
        response_type: "token id_token",
        redirect_uri: env.APP_URL,
        scope: ALL_SCOPES.join(" "),
        audience: env.IDP_AUDIENCE,
        mode: "employee",
    }).toString()

    return `${env.IDP_BASE_URL}/authorize?${query}`
}

/**
 * Get the access token from local storage
 * @returns Access token
 */
export const getToken = (): string | undefined =>
    localStorage?.getItem(ACCESS_TOKEN_KEY) ?? undefined

/**
 * Set the access token to local storage
 */
export function setToken(token: string): void {
    localStorage?.setItem(ACCESS_TOKEN_KEY, token)
}

/**
 * Remove the access token to local storage
 */
export function removeToken(): void {
    localStorage?.removeItem(ACCESS_TOKEN_KEY)
}

/**
 * Get link to IDP logout resource.
 */
export function getLogoutLink(): string {
    return `${env.IDP_BASE_URL}/logout?client_id=${env.IDP_CLIENT_ID}&redirect_uri=${env.APP_URL}`
}

/**
 * Check if the token has expired
 */
export const hasTokenExpired = () => {
    const token = getToken()

    if (!token) {
        return true
    }

    const jwt = jwtDecode<{ exp?: number; sub?: string }>(token)

    if (jwt.sub) {
        datadogLogs.addLoggerGlobalContext("user.id", jwt.sub)
    }

    const currentTime = Date.now()
    const expiration = (jwt.exp ?? 0) * 1000

    return currentTime > expiration
}

/**
 * Check if the URL contains an access token (`#access_token=`).
 */
export const isTokenInHash = (): boolean => {
    const hash = window.location.hash.substring(1)
    return hash.includes("access_token")
}

/**
 * Get the access token from the URL (`#access_token=`).
 */
export const getTokenFromHash = (): string => {
    const hash = window.location.hash.substring(1)
    const hashToken = hash.split("=")[1]
    if (!hashToken) throw new Error("No hashtoken found")
    const token = hashToken.split("&")[0]
    if (!token) throw new Error("No token found")
    return token
}

/**
 * Check token and expiry
 */
export const isAuthorized = (): boolean => !!getToken() && !hasTokenExpired()

/**
 * Validate token has a given scope
 */
export const hasPermission = (scope: Scope): boolean => {
    const token = getToken()

    if (!token) {
        return false
    }

    const scopes = (jwtDecode(token) as AccessToken).scopes
    return scopes.includes(scope)
}

/**
 * Get user's ID
 */
export const getUserId = (): string | undefined => {
    const accessToken = getToken()

    if (!accessToken) {
        return
    }

    const { sub } = jwtDecode(accessToken) as { sub?: string }

    return sub
}
